import {
  hoverStyle,
  pressableOpacity,
  PressableVariants
} from '@unistocks/components/basic/variants';
import { Text } from '@unistocks/components/text/text';
import { SortOrder } from '@unistocks/proxy/axios-client';
import type { GetProps, SizeTokens } from 'tamagui';
import { createStyledContext, styled, ThemeableStack, withStaticProperties } from 'tamagui';

export const getLocation = (index: number, array: any[]): Location => {
  if (index === array.length - 1) return 'last';
  if (index === 0) return 'first';
  return 'middle';
};

export type AlignCells = {
  y?: 'center' | 'start' | 'end';
  x?: 'center' | 'start' | 'end';
};

export type AlignHeaderCells = AlignCells;

export type Location = 'first' | 'middle' | 'last';

export type Sorting<TProps = string> = {
  prop: TProps;
  direction: SortOrder;
};

export type TableProps = GetProps<typeof TableFrame>;
export type TableRowProps = GetProps<typeof TableRow>;
export type TableCellProps = GetProps<typeof TableCell>;
export type HeaderCellProps = GetProps<typeof HeaderCell>;
export type HeaderCellTextProps = GetProps<typeof HeaderCellText>;
export type TableBodyProps = GetProps<typeof TableBody>;
export type TableHeadProps = GetProps<typeof TableHead>;
export type TableFootProps = GetProps<typeof TableFoot>;

const TableContext = createStyledContext<{
  cellWidth: SizeTokens | number;
  cellHeight: SizeTokens | number;
  alignHeaderCells: AlignCells;
  alignCells: AlignCells;
  borderColor: string;
  displayType: 'flex' | 'table';
}>({
  cellWidth: '$10',
  cellHeight: '$4',
  alignHeaderCells: { x: 'start', y: 'center' },
  alignCells: { x: 'start', y: 'center' },
  borderColor: '$borderColor',
  displayType: 'flex'
});

/** Table Components */
const TableRow = styled(ThemeableStack, {
  context: TableContext,
  render: 'tr',
  fd: 'row',
  justifyContent: 'space-between',

  variants: {
    displayType: {
      table: {
        '$platform-web': {
          display: 'table-row'
        }
      }
    },

    rowLocation: {
      first: () => {
        return {
          borderBottomWidth: '$0.5'
        };
      },
      last: () => {
        return {
          borderBottomWidth: 0
        };
      },
      middle: () => {
        return {
          borderBottomWidth: '$0.5'
        };
      }
    },

    ...PressableVariants
  } as const,

  defaultVariants: {
    pressable: false,
    hoverable: false
  }
});

const TableCell = styled(ThemeableStack, {
  context: TableContext,
  render: 'td',
  fd: 'row',
  flexGrow: 1,
  px: '$3',
  overflow: 'hidden',

  variants: {
    displayType: {
      table: {
        '$platform-web': {
          display: 'table-cell'
        }
      }
    },

    cellWidth: {
      '...size': (name, { tokens }) => {
        return {
          width: tokens.size[name as keyof typeof tokens.size]
        };
      },
      ':number': width => ({ width })
    },

    cellHeight: {
      '...size': (name, { tokens }) => {
        return {
          minHeight: tokens.size[name as keyof typeof tokens.size]
        };
      },
      ':number': width => ({ width })
    },

    alignCells: (val: AlignCells) => {
      return {
        alignItems: !val.y || val.y === 'center' ? 'center' : `flex-${val.y}`,
        justifyContent: val.x ? (val.x === 'center' ? 'center' : `flex-${val.x}`) : 'start'
      };
    },

    cellLocation: {
      first: () => {
        return {};
      },
      last: () => {
        return {
          borderLeftWidth: 0.5
        };
      },
      middle: () => {
        return {
          borderLeftWidth: 0.5
        };
      }
    }
  } as const
});

const HeaderCell = styled(ThemeableStack, {
  context: TableContext,
  render: 'th',
  fd: 'row',
  flexGrow: 1,
  py: '$2',
  px: '$2',
  borderColor: '$color6',

  variants: {
    displayType: {
      table: {
        '$platform-web': {
          display: 'table-cell'
        }
      }
    },

    cellWidth: {
      '...size': (name, { tokens }) => {
        return {
          width: tokens.size[name as keyof typeof tokens.size]
        };
      },
      ':number': width => ({ width })
    },

    alignHeaderCells: (val: AlignHeaderCells) => {
      return {
        alignItems: val.y === 'center' ? 'center' : `flex-${val.y}`,
        justifyContent: val.x === 'center' ? 'center' : `flex-${val.x}`
      };
    },

    cellLocation: {
      first: () => {
        return {
          borderTopLeftRadius: '$3'
        };
      },
      last: () => {
        return {
          borderLeftWidth: 1,
          borderTopRightRadius: '$3'
        };
      },
      middle: () => {
        return {
          borderLeftWidth: '$0.5'
        };
      }
    },

    sortable: {
      true: {
        cursor: 'pointer',
        hoverStyle: hoverStyle,
        pressStyle: pressableOpacity.pressStyle
      }
    }
  } as const
});

const HeaderCellText = styled(Text, {
  color: '$headingFont',
  size: '$5',
  fw: '$7'
});

const TableBody = styled(ThemeableStack, {
  context: TableContext,
  render: 'tbody',
  fd: 'column',
  flexShrink: 1,

  variants: {
    displayType: {
      table: {
        '$platform-web': {
          display: 'table-row-group'
        }
      }
    },

    // 1 = right, 0 = nowhere, -1 = left
    going: {
      ':number': going => ({
        enterStyle: {
          x: going > 0 ? '$10' : '$-10',
          opacity: 0
        },
        exitStyle: {
          zIndex: 0,
          x: going < 0 ? '$10' : '$-10',
          opacity: 0
        }
      })
    }
  } as const
});

const TableHead = styled(ThemeableStack, {
  context: TableContext,
  render: 'thead',
  fd: 'column',
  flexShrink: 1,
  bg: '$color3',
  borderTopLeftRadius: '$3',
  borderTopRightRadius: '$3',

  variants: {
    displayType: {
      table: {
        '$platform-web': {
          display: 'table-header-group'
        }
      }
    }
  } as const
});

const TableFoot = styled(ThemeableStack, {
  context: TableContext,
  render: 'tfoot',
  fd: 'column',
  flexShrink: 1,
  borderTopWidth: 1,
  bg: '$color3',
  borderBottomLeftRadius: '$3',
  borderBottomRightRadius: '$3',

  variants: {
    displayType: {
      table: {
        '$platform-web': {
          display: 'table-footer-group'
        }
      }
    },

    bordered: {
      true: {
        borderTopWidth: 1
      }
    }
  } as const
});

const TableFrame = styled(ThemeableStack, {
  context: TableContext,
  render: 'table',
  bg: '$background',

  variants: {
    displayType: {
      table: {
        '$platform-web': {
          display: 'table'
        }
      }
    },

    /** just added these empty variants to avoid ts erros on Table */
    cellWidth: {
      '...size': () => {
        return {};
      }
    },
    cellHeight: {
      '...size': () => {
        return {};
      }
    },
    alignHeaderCells: val => ({}),
    alignCells: val => ({})
  } as const
});

export type TableActionsCellProps = TableCellProps;

const TableActionsCell: React.FC<TableActionsCellProps> = ({ children, ...props }) => (
  <TableCell
    alignCells={{ x: 'end' }}
    cellLocation="last"
    onStartShouldSetResponder={() => true}
    onPress={e => e?.stopPropagation?.()}
    {...props}
  >
    {children}
  </TableCell>
);

export const Table = withStaticProperties(TableFrame, {
  Head: TableHead,
  Body: TableBody,
  Row: TableRow,
  Cell: TableCell,
  ActionsCell: TableActionsCell,
  HeaderCell,
  HeaderCellText,
  Foot: TableFoot
});
